import React, {useEffect, useEffectEvent, useLayoutEffect, useRef, useState} from "react";
import PropTypes from "prop-types";

import {Portal} from "./Portal";

// Reusable context menu that positions itself relative to an anchor element.
// Keeps at least 16px spacing from the viewport and tries to center on the anchor button.
export function ContextMenu(props) {
  const menuRef = useRef(null);
  const [style, setStyle] = useState({ visibility: "hidden", opacity: 0 });

  const onClose = useEffectEvent(() => {
    props.onClose?.();
  });

  // Close on outside click and Escape
  useEffect(() => {
    if (!props.isOpen) {
      return;
    }

    function handleClickOutside(event) {
      if (!menuRef.current) return;
      if (menuRef.current.contains(event.target)) return;
      if (props.anchorElement && props.anchorElement.contains && props.anchorElement.contains(event.target)) return;
      onClose();
    }

    function handleKey(event) {
      if (event.key === "Escape") onClose();
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [props.isOpen, props.anchorElement, onClose]);

  // Compute position whenever opened or on resize/scroll
  useLayoutEffect(() => {
    if (!props.isOpen) return;
    const menuElement = menuRef.current;
    if (!menuElement || !props.anchorElement) return;

    function computePosition() {
      const margin = 16;
      const anchorRect = props.anchorElement.getBoundingClientRect();
      const menuRect = menuElement.getBoundingClientRect();

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Center horizontally on anchor, clamped with margin
      const anchorCenterX = anchorRect.left + anchorRect.width / 2;
      let left = anchorCenterX - menuRect.width / 2;
      left = Math.max(margin, Math.min(left, viewportWidth - margin - menuRect.width));

      // Prefer below; if not enough space, place above
      const spaceBelow = viewportHeight - anchorRect.bottom - margin;
      const spaceAbove = anchorRect.top - margin;
      let top;
      const offset = 8; // small offset from anchor
      if (spaceBelow >= menuRect.height + offset) {
        top = anchorRect.bottom + offset;
      } else if (spaceAbove >= menuRect.height + offset) {
        top = anchorRect.top - menuRect.height - offset;
      } else {
        // Clamp within viewport if neither fits
        top = Math.max(margin, Math.min(anchorRect.bottom + offset, viewportHeight - margin - menuRect.height));
      }

      // Convert to page coordinates (account for scroll)
      const pageLeft = left + window.scrollX;
      const pageTop = top + window.scrollY;

      setStyle({
        position: "absolute",
        left: `${pageLeft}px`,
        top: `${pageTop}px`,
        visibility: "visible",
        opacity: 1,
      });
    }

    computePosition();
    window.addEventListener("resize", computePosition);
    window.addEventListener("scroll", computePosition, true);
    return () => {
      window.removeEventListener("resize", computePosition);
      window.removeEventListener("scroll", computePosition, true);
    };
  }, [props.isOpen, props.anchorElement]);

  if (!props.isOpen) return null;

  return (
    <Portal>
      <div className="account-context-menu" ref={menuRef} style={style} role="menu">
        {props.children}
      </div>
    </Portal>
  );
}

ContextMenu.propTypes = {
  isOpen: PropTypes.bool,
  anchorElement: PropTypes.instanceOf(Element),
  onClose: PropTypes.func,
  children: PropTypes.node,
};
